import type { Layout, PaletteModel } from '../../../data/types';
import type { Rng } from '../../../core/rng';
import { worldToLayout } from '../../../core/coords';
import { closestPointOnPolyline } from '../../../geo/polyline';
import type { Vec2 } from '../../../geo/types';
import type { ChunkSink } from '../../mesh/chunker';
import { drapedBuilder } from '../../mesh/drapedBuilder';
import { elevatedBuilder } from '../../mesh/elevatedBuilder';
import type { Obstacle } from '../../types';
import { edgeIdFor } from '../../mesh/attributes';
import { sampleSwatch } from '../../../data/palette';

import { buildWall } from './walls';
import { buildTower, towerFacing, towerPolygon, type TowerSetting } from './towers';
import { layoutWaterTest } from './moat';
import { wallOutwardFields } from './wallFacing';
import { buildGate } from './gates';
import { nicheAlbedo, wallAlbedo } from './wallAlbedo';
import { hexToLinear } from '../../../core/color';
import { nearestCentre } from '../landmarks/paletteCentres';
import { PAINTED_GATE } from '../landmarks/fortress/sintAntoniespoortPlan';
import { towerHasSlateRoof } from './roofs';

export interface FortificationBuildResult {
  readonly triangles: number;
  readonly obstacles: readonly Obstacle[];
  readonly wallCount: number;
  readonly towerCount: number;
  readonly gateCount: number;
}

/**
 * `elevationAt` is the ground's height at a layout point; without it the ground is level at zero.
 * A tower and a gate are lifted whole onto the ground at their own place. A wall runs too far for
 * that: every point of it stands on the ground under the nearest point of its line.
 */
export function buildFortifications(
  layout: Layout,
  chunks: ChunkSink,
  palette: PaletteModel,
  rng: Rng,
  warn: (msg: string) => void,
  elevationAt: (p: Vec2) => number = () => 0
): FortificationBuildResult {
  const { walls, towers, gates } = layout.fortifications;

  const obstacles: Obstacle[] = [];
  let totalTriangles = 0;
  let edgeIdBase = 0;

  /* The painted medians, the walls' with the painter's light taken out (wallAlbedo.ts): one
     swatch per wall and tower forked by id landed further from the painting in the painter view,
     and one swatch shared by all further still (STYLE §8). */
  const wallColor = wallAlbedo(palette);
  const nicheColor = nicheAlbedo(palette);
  const isWater = layoutWaterTest(layout.water);
  const outward = wallOutwardFields(walls, isWater);
  const towerColor = palette.towerStone.median.linear;
  const roofColor = sampleSwatch(palette.roofTile, rng.fork('roofColor'));
  const slateColor = sampleSwatch(palette.roofSlate, rng.fork('slateColor'));
  const plan = {
    planWall: nearestCentre(palette.wallBrick, hexToLinear(PAINTED_GATE.wall)),
    slate: nearestCentre(palette.roofSlate, hexToLinear(PAINTED_GATE.roof)),
  };

  for (const wall of walls) {
    const wallRng = rng.fork(`wall:${wall.id}`);
    const pathMid = wall.path[Math.floor(wall.path.length / 2)]!;
    /* The ground is read on the wall's own line, so its foot and its courses lie level across its
       thickness and follow the ground along its length. */
    const builder = drapedBuilder(chunks.builderAt('fabric', pathMid), (world) =>
      elevationAt(closestPointOnPolyline(wall.path, worldToLayout(world)).point)
    );

    const edgeId = edgeIdFor(edgeIdBase++);
    const seed = wallRng.next();

    const facing = outward.get(wall.id);
    const result = buildWall(builder, wall, gates, wallColor, roofColor, edgeId, seed, {
      isWater,
      nicheColor,
      ...(facing ? { outward: facing } : {}),
    });

    totalTriangles += result.triangles;
    result.pieces.forEach((polygon, i) => {
      obstacles.push({ id: `${wall.id}:${i}`, kind: 'fortification', polygon });
    });
  }

  const setting: TowerSetting = { walls, isWater, timberColor: palette.shipHull.median.linear };

  for (const tower of towers) {
    const towerRng = rng.fork(`tower:${tower.id}`);
    if (towerFacing(tower, setting).withoutMoat) {
      warn(
        `${tower.id}: no water beside its wall, so its round side turns away from the wall line`
      );
    }
    const builder = elevatedBuilder(chunks.builderAt('fabric', tower.pos), elevationAt(tower.pos));

    const edgeId = edgeIdFor(edgeIdBase++);
    const seed = towerRng.next();

    const towerRoof = towerHasSlateRoof(tower) ? slateColor : roofColor;
    const triangles = buildTower(builder, tower, towerColor, towerRoof, edgeId, seed, setting);

    totalTriangles += triangles;
    obstacles.push({
      id: tower.id,
      kind: 'fortification',
      polygon: towerPolygon(tower, setting),
    });
  }

  for (const gate of gates) {
    const gateRng = rng.fork(`gate:${gate.id}`);
    const builder = elevatedBuilder(chunks.builderAt('fabric', gate.pos), elevationAt(gate.pos));

    const edgeId = edgeIdFor(edgeIdBase++);
    const seed = gateRng.next();

    const trim = palette.highlightWhite.median.linear;
    const colours = {
      wall: towerColor,
      roof: roofColor,
      dark: nicheColor,
      trim,
      timber: palette.shipHull.median.linear,
      ...plan,
    };
    const result = buildGate(builder, gate, colours, edgeId, seed);

    totalTriangles += result.triangles;
    result.obstacles.forEach((piece, i) => {
      obstacles.push({ id: `${gate.id}:${i}`, kind: 'fortification', polygon: piece.outer });
    });
  }

  return {
    triangles: totalTriangles,
    obstacles,
    wallCount: walls.length,
    towerCount: towers.length,
    gateCount: gates.length,
  };
}

export { collectFortificationReserved } from './reserved';
